import { useMemo } from "react";
import { PIECE_SRC } from "./pieces";

const FILES = "abcdefgh";

interface Props {
  /** Position as a FEN string. */
  fen: string;
  /** "w" = white at the bottom. */
  orientation: "w" | "b";
  /** Legal destinations per origin square (from the server). */
  moves: Record<string, string[]>;
  selected: string | null;
  onSelect: (sq: string | null) => void;
  onMove: (from: string, to: string) => void;
  /** Highlight the last move played. */
  lastMove?: { from: string; to: string } | null;
  disabled: boolean;
  /** Flash a wrong move red. */
  wrongSquare?: string | null;
}

function boardFromFen(fen: string): (string | null)[][] {
  const placement = fen.split(" ")[0] ?? "";
  return placement.split("/").map((rank) => {
    const row: (string | null)[] = [];
    for (const ch of rank) {
      if (/[1-8]/.test(ch)) for (let i = 0; i < Number(ch); i++) row.push(null);
      else row.push(ch);
    }
    return row;
  });
}

export function ChessBoard({
  fen,
  orientation,
  moves,
  selected,
  onSelect,
  onMove,
  lastMove,
  disabled,
  wrongSquare,
}: Props) {
  const board = useMemo(() => boardFromFen(fen), [fen]);
  const ranks = orientation === "w" ? [0, 1, 2, 3, 4, 5, 6, 7] : [7, 6, 5, 4, 3, 2, 1, 0];
  const files = orientation === "w" ? [0, 1, 2, 3, 4, 5, 6, 7] : [7, 6, 5, 4, 3, 2, 1, 0];
  // Destinations reachable from the selected square (promotion suffix stripped).
  const targets = useMemo(
    () => new Set((selected ? (moves[selected] ?? []) : []).map((t) => t.slice(0, 2))),
    [selected, moves],
  );

  const tap = (sq: string, piece: string | null) => {
    if (disabled) return;
    if (selected && targets.has(sq)) return onMove(selected, sq);
    if (piece && moves[sq]?.length) return onSelect(sq === selected ? null : sq);
    onSelect(null);
  };

  return (
    <div className="mx-auto aspect-square w-full max-w-[min(92vw,420px)] overflow-hidden rounded-xl shadow-lg">
      <div className="grid h-full w-full grid-cols-8 grid-rows-8">
        {ranks.map((r) =>
          files.map((f) => {
            const sq = `${FILES[f]}${8 - r}`;
            const piece = board[r]?.[f] ?? null;
            const dark = (r + f) % 2 === 1;
            const isTarget = targets.has(sq);
            const isSelected = sq === selected;
            const isLast = lastMove && (lastMove.from === sq || lastMove.to === sq);
            const isWrong = sq === wrongSquare;
            return (
              <button
                key={sq}
                type="button"
                disabled={disabled}
                onClick={() => tap(sq, piece)}
                className={`relative flex items-center justify-center ${
                  dark ? "bg-[#7f9f6b]" : "bg-[#eeeed2]"
                } ${isSelected ? "outline outline-[3px] -outline-offset-[3px] outline-[#f0c419]" : ""} ${
                  isWrong ? "outline outline-[3px] -outline-offset-[3px] outline-[#c94f4f]" : ""
                }`}
                aria-label={sq}
              >
                {isLast && <span className="absolute inset-0 bg-[#f0c419]/25" />}
                {isTarget && !piece && (
                  <span className="absolute h-[26%] w-[26%] rounded-full bg-black/25" />
                )}
                {isTarget && piece && (
                  <span className="absolute inset-[6%] rounded-full border-[3px] border-black/25" />
                )}
                {piece && (
                  <img
                    src={PIECE_SRC[piece]}
                    alt=""
                    draggable={false}
                    className="relative h-[92%] w-[92%] select-none"
                    style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,.35))" }}
                  />
                )}
              </button>
            );
          }),
        )}
      </div>
    </div>
  );
}
